import { Alert, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { BetaFlag } from "portal/components/BetaFlag";
import { BlockRange, getBreakpoints } from "portal/utils/blocks";
import { BooleanMeasurement } from "../../measurement/BooleanMeasurement";
import { classes } from "portal/theme/theme";
import { formatMeasurement } from "portal/components/measurement/formatters";
import { getSpatialMetricName, SpatialMetric } from "portal/utils/metrics";
import { isEmpty } from "portal/utils/arrays";
import {
  isSpatialOneOf,
  resolveSpatialMetric,
  SPATIAL_COVERAGE,
  SPATIAL_THINNING_METRICS,
  SPATIAL_WEEDING_METRICS,
  SpatialMetricSelection,
} from "portal/utils/spatialMetrics";
import { JigsawRobotHistory } from "../../../views/robots/useRobotHistory";
import { LOCALSTORAGE_MAP_LEGEND } from "portal/utils/localStorage";
import { Measurement } from "../../measurement/Measurement";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import LegendToggleIcon from "@mui/icons-material/LegendToggleOutlined";
import MinimizeIcon from "@mui/icons-material/ArrowOutwardOutlined";
import React, {
  Dispatch,
  FunctionComponent,
  ReactNode,
  SetStateAction,
  useMemo,
} from "react";
import RulerIcon from "@mui/icons-material/StraightenOutlined";

interface Props {
  className?: string;
  onOpenMenu?: () => void;
  selection: SpatialMetricSelection;
  isRelative: boolean;
  range?: BlockRange;
  setRelative: Dispatch<SetStateAction<boolean>>;
  jigsawBlocks?: JigsawRobotHistory;
}

export const HeatmapLegend: FunctionComponent<Props> = ({
  className,
  onOpenMenu,
  selection,
  isRelative,
  range,
  setRelative,
  jigsawBlocks,
}) => {
  const { t, i18n } = useTranslation();
  const { measurementSystem } = useSelf();

  const [isLegendVisible, setLegendVisible] = useLocalStorage<boolean>(
    LOCALSTORAGE_MAP_LEGEND,
    true
  );

  const selectedMetric = useMemo<SpatialMetric | undefined>(
    () => resolveSpatialMetric(selection),
    [selection]
  );

  if (!selectedMetric || !selectedMetric.canMap) {
    return;
  }

  if (selectedMetric === SPATIAL_COVERAGE) {
    return;
  }

  const { colors, id } = selectedMetric;

  const categoriesList =
    selectedMetric.categoryKind === "crop"
      ? jigsawBlocks?.crops
      : jigsawBlocks?.weeds;
  const categoryName = (categoriesList ?? []).find(
    (cat) => cat.id === selectedMetric.categoryId
  )?.localizedName;

  // Weed category layers are titled by the category name alone; crop
  // category layers pair the category name with the metric name. While the
  // category's name lookup is in flight, show a skeleton instead of the
  // "Unknown (id)" fallback the names map holds until then.
  let title: ReactNode;
  if (categoryName !== undefined && selectedMetric.categoryKind === "weed") {
    title = categoryName;
  } else {
    // resolveSpatialMetric never sets displayName, so this is the bare metric
    // name; the category name is composed around it below
    const metricName = getSpatialMetricName(t, selectedMetric);
    title =
      categoryName === undefined
        ? metricName
        : t("components.map.heatmaps.categoryMetricTitle", {
            category: categoryName,
            metric: metricName,
          });
  }

  const breakpoints = getBreakpoints(colors, isRelative);
  const isUnknown = breakpoints.every((breakpoint) => breakpoint === 0);
  const isNotWeeding = isSpatialOneOf(id, SPATIAL_WEEDING_METRICS) && isUnknown;
  const isNotThinning =
    isSpatialOneOf(id, SPATIAL_THINNING_METRICS) && isUnknown;

  let content = <></>;

  if (isEmpty(breakpoints) || isEmpty(colors.colors)) {
    content = (
      <Alert severity="error">
        {t("components.map.heatmaps.errors.legend")}
      </Alert>
    );
  } else if (isNotWeeding) {
    content = (
      <span className="font-bold text-md text-gray-600">
        {t("components.map.heatmaps.errors.notWeeding")}
      </span>
    );
  } else if (isNotThinning) {
    content = (
      <span className="font-bold text-md text-gray-600">
        {t("components.map.heatmaps.errors.notThinning")}
      </span>
    );
  } else if (isUnknown) {
    content = (
      <span className="font-bold text-md text-gray-600">
        {t("components.map.heatmaps.errors.unknown")}
      </span>
    );
  } else {
    // the relative scale is clamped to the 10th/90th percentiles, so show the
    // true extremes at either end of the bar
    const extremes =
      !colors.discrete && (isRelative || !colors.absoluteBreakpoints)
        ? range
        : undefined;
    const unit = "units" in selectedMetric ? selectedMetric.units : undefined;
    content = (
      <>
        {colors.discrete ? (
          <div className="flex justify-around w-full gap-3">
            {colors.colors.map((color, index) => {
              const label = colors.labels?.[index];
              return (
                <div
                  key={color.toString()}
                  className="flex flex-col items-center gap-1 text-center text-xs"
                >
                  <div
                    className="w-5 h-5"
                    style={{
                      background: color.toString(),
                      printColorAdjust: "exact",
                      WebkitPrintColorAdjust: "exact",
                    }}
                  />
                  {selectedMetric.type === "boolean" ? (
                    <BooleanMeasurement
                      i18nKey={selectedMetric.i18nKey}
                      value={breakpoints[index]}
                    />
                  ) : (
                    <>
                      {label ? (
                        <span className="whitespace-nowrap">
                          {t(label.i18nKey)}
                        </span>
                      ) : undefined}
                      {label?.captionI18nKey ? (
                        <span className="whitespace-nowrap text-carbon-gray-light">
                          {t(label.captionI18nKey)}
                        </span>
                      ) : undefined}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="w-full rounded-full h-5 color"
            style={{
              background: `linear-gradient(to right, ${colors.colors.join(
                ", "
              )})`,
              printColorAdjust: "exact",
              WebkitPrintColorAdjust: "exact",
            }}
          />
        )}
        {!colors.discrete && (
          <div className="flex justify-between w-full">
            {breakpoints.map((value, index, labels) => {
              const isFirst = index === 0;
              const isLast = index === labels.length - 1;
              const isMiddle = !isFirst && !isLast;
              const label = colors.labels?.[index];
              return (
                <div
                  key={index}
                  className={classes(
                    "text-xs whitespace-nowrap flex flex-col items-center",
                    {
                      "pr-3": isFirst,
                      "px-3": isMiddle,
                      "pl-3": isLast,
                    }
                  )}
                  style={{
                    width: `1/${labels.length}`,
                  }}
                >
                  {label && !isRelative && <span>{t(label.i18nKey)}</span>}
                  {selectedMetric.type === "boolean" ? (
                    <BooleanMeasurement
                      i18nKey={selectedMetric.i18nKey}
                      value={value}
                    />
                  ) : (
                    <Measurement
                      cycleSlot={selectedMetric.cycleSlot}
                      unit={unit}
                      value={value}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {extremes && (
          <>
            <div className="w-full border-0 border-t border-solid border-darken-300" />
            <div className="flex items-baseline justify-between w-full gap-4 text-xs">
              <div className="flex items-baseline gap-1 opacity-50 whitespace-nowrap">
                <RulerIcon className="text-base translate-y-1" />
                <span>{t("components.map.heatmaps.dataRange")}</span>
              </div>
              <div className="flex items-baseline gap-1 whitespace-nowrap">
                <Measurement
                  cycleSlot={selectedMetric.cycleSlot}
                  label="none"
                  unit={unit}
                  value={extremes.min}
                />
                <span>-</span>
                <Measurement
                  cycleSlot={selectedMetric.cycleSlot}
                  unit={unit}
                  value={extremes.max}
                />
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <>
      {!isLegendVisible && (
        <Tooltip title="Show Legend" placement="left" arrow>
          <div
            className={classes(
              "absolute top-3 right-3 md:hidden",
              "mapboxgl-ctrl mapboxgl-ctrl-group w-fit"
            )}
          >
            <button onClick={() => setLegendVisible(true)}>
              <LegendToggleIcon />
            </button>
          </div>
        </Tooltip>
      )}
      <div
        className={classes(
          "absolute top-[10px] right-[10px] z-[2]",
          "text-black bg-white rounded",
          "p-2 flex flex-col items-center gap-2",
          "cursor-default overflow-hidden",
          { "hidden sm:flex": !isLegendVisible },
          className
        )}
      >
        <BetaFlag title={t("components.BetaFlag.spatial.description")} />
        <div className="text-center font-bold flex items-center justify-center h-6 px-5">
          <span
            onClick={() => onOpenMenu?.()}
            className="reset"
            role="button"
            tabIndex={0}
          >
            {title}
          </span>
          <MinimizeIcon
            className="ml-1 -mr-[20px] cursor-pointer print:hidden md:hidden"
            onClick={() => setLegendVisible(false)}
          />
        </div>
        <div className="text-center text-xs italic -mt-2">
          {t("utils.metrics.spatial.heatmapWarning", {
            area: formatMeasurement(t, i18n, measurementSystem, 400, "ft2"),
          })}
        </div>
        {isNotThinning}
        {content}

        {colors.absoluteBreakpoints && !colors.discrete && (
          <FormControlLabel
            className={classes("flex-shrink-0 print:hidden")}
            control={
              <Checkbox
                checked={isRelative}
                classes={{ root: "text-black" }}
                color="primary"
                onChange={(_event, checked) => setRelative(checked)}
              />
            }
            label={
              <span className="whitespace-nowrap text-xs text-black">
                {t("components.map.heatmaps.relative")}
              </span>
            }
          />
        )}
      </div>
    </>
  );
};
